import React, { useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { BookingsToEditSortColumn, defaultBookingsListSortState, toggleBookingsListSort } from 'store/bookingsFilterSlice';
import SortableBookingTableHeader from 'components/table/SortableBookingTableHeader';
import PlusIcon from '@mui/icons-material/Add';
import { PDF_IMPORT_ENABLED_QUERY } from './pdf-import';
import OpenIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import {
  CircularProgress,
  Alert,
  Grid,
  Button,
  IconButton,
  TextField,
  Badge,
  Tooltip,
  Box,
  FormControlLabel,
  Checkbox,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import { Helmet } from 'react-helmet';
import * as Sentry from '@sentry/react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  REFETCH_OFFER_QUERIES,
  EVICT_OFFER_QUERIES,
  OFFERVERSION_DELETE_MUTATION,
  OFFER_SEND_REMINDER_CLIENT_MUTATION,
  OFFERVERSIONS_LIST_IDS_QUERY,
  OFFERVERSIONS_LIST_BY_IDS_QUERY,
  OFFER_SEMINARGO_APPROVAL_ENABLED_QUERY,
} from './gql';
import {
  EHLBookingStatus,
  EOfferSource,
  EOfferStatus,
  EOfferVersionStatus,
  EOfferVersionType,
  ListWlOfferVersionsByIdsQuery,
  ListWlOfferVersionsIdsQueryVariables,
  WLOfferVersionListShortFragment,
} from '__generated__/graphql';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import SimpleAccordion from 'components/SimpleAccordion';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { FormatDateTime, FormatDateRange } from 'components/DateTime';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/ForwardToInbox';
import { filterSelector, combinedBookingViewSelector, bookingModeSelector } from 'helper/filter';
import { setCombinedBookingView, setBookingMode } from 'store/filterSlice';
import { RedirectError } from 'pages/error';
import { userSelector } from 'helper/security';
import { EBookingMode } from 'types';
import PagedTable from 'components/table/PagedTable';

// Premium/Classic customer indicator component
const CustomerTypeIndicator = ({ isPremium }: { isPremium?: boolean }) => (
  <Tooltip title={isPremium ? String(i18next.t('filter-customer-type-premium')) : String(i18next.t('filter-customer-type-classic'))} arrow>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        minWidth: '20px',
        minHeight: '20px',
        flexShrink: 0,
        borderRadius: '50%',
        border: isPremium ? '2px solid' : '2px solid #6c757d',
        borderColor: isPremium ? 'primary.main' : '#6c757d',
        backgroundColor: 'transparent',
        color: isPremium ? 'primary.main' : '#6c757d',
        fontSize: '11px',
        fontWeight: 'bold',
        lineHeight: '1',
        marginRight: '8px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        cursor: 'help',
      }}
    >
      {isPremium ? 'P' : 'C'}
    </Box>
  </Tooltip>
);

const StyledBadge = ({ count, showCount = true }: { count: React.ReactNode; showCount?: boolean }) => (
  <Badge
    badgeContent={showCount ? count : undefined}
    variant={showCount ? 'standard' : 'dot'}
    max={99999}
    sx={{
      '& .MuiBadge-badge': {
        backgroundColor: 'primary.main',
        color: 'white',
        ...(showCount
          ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '28px',
              height: '20px',
              padding: '0 6px',
              transition: 'all 0.2s ease-in-out',
            }
          : {
              height: '12px',
              width: '12px',
              borderRadius: '6px',
              minWidth: '12px',
              padding: 0,
            }),
      },
    }}
  />
);

const OfferVersionListTable = ({
  title,
  autoExpanded,
  showEventTitle,
  showDeleteReason,
  queryVariables,
  querySkip,
  defaultPageSize,
  onRowClick,
  setShowDeleteDialog,
  getHotelLinkStyle,
  getBookingIdStyle,
}: {
  title: string;
  autoExpanded?: boolean;
  showEventTitle?: boolean;
  showDeleteReason?: boolean;
  queryVariables: ListWlOfferVersionsIdsQueryVariables;
  querySkip: boolean;
  defaultPageSize?: 10 | 20 | 50;
  onRowClick?: (row: WLOfferVersionListShortFragment) => void;
  setShowDeleteDialog?: (row: WLOfferVersionListShortFragment) => void;
  getHotelLinkStyle: (source: EOfferSource) => React.CSSProperties;
  getBookingIdStyle: (source: EOfferSource) => React.CSSProperties;
}) => {
  const dispatch = useDispatch();
  const [showTable, setShowTable] = useState(!!autoExpanded);
  const [{ sortColumn, sortDirection }, setSortState] = useState(defaultBookingsListSortState);

  const _canArchive = (row: WLOfferVersionListShortFragment) => {
    return setShowDeleteDialog && row.offer.source === EOfferSource.WIDGET;
  };

  const { data, loading, error, refetch } = useQuery(OFFERVERSIONS_LIST_IDS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      ...queryVariables,
      sortColumn,
      sortDirection,
    },
    skip: querySkip,
  });

  const [refetchOfferVersions, { data: offerVersionsData, loading: offerVersionsLoading, error: offerVersionsError }] = useLazyQuery(
    OFFERVERSIONS_LIST_BY_IDS_QUERY,
    {
      fetchPolicy: 'network-only',
    },
  );
  const [sendOfferReminderEmailClient] = useMutation(OFFER_SEND_REMINDER_CLIENT_MUTATION);
  const handleSort = (column: BookingsToEditSortColumn) => {
    setSortState(prev => toggleBookingsListSort(prev, column));
  };

  const _formatDateRange = (row: ListWlOfferVersionsByIdsQuery['listWlOfferVersionsByIds'][0]) => (
    <FormatDateRange from={row.input?.startDate || row.inputLister?.startDate} to={row.input?.endDate || row.inputLister?.endDate} />
  );
  const _showSendOfferReminderEmailClient = (row: WLOfferVersionListShortFragment) => {
    return (
      row.offer.source === EOfferSource.WIDGET &&
      (row.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL || row.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL)
    );
  };

  return (
    <SimpleAccordion
      autoOpen={!!autoExpanded}
      onExpandedChange={expanded => setShowTable(showTable || expanded)}
      header={
        <Grid container alignItems="center" spacing={2} sx={{ '& > .MuiGrid-item:last-child': { marginLeft: 1 } }}>
          <Grid item>{title}</Grid>
          <Grid item>{loading || !data ? <CircularProgress size={16} /> : <StyledBadge count={data.listWlOfferVersions.length} />}</Grid>
        </Grid>
      }
    >
      {showTable && (
        <PagedTable
          filterKey={`offer-versions-${JSON.stringify(queryVariables)}-${sortColumn}-${sortDirection}`}
          refetchPage={(skip, take) => {
            const ids = (data?.listWlOfferVersions?.map(v => v.id) || []).slice(skip, skip + take);
            refetchOfferVersions({
              variables: {
                ids,
              },
            });
          }}
          defaultPageSize={defaultPageSize}
          rowsLoading={loading || offerVersionsLoading}
          rowsErr={error || offerVersionsError}
          customHeaders={
            <TableHead>
              <TableRow>
                <SortableBookingTableHeader
                  column="createdAt"
                  label={i18next.t('offers-list-header-createdat')}
                  currentSortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <TableCell sx={{ borderBottom: 'none' }}>{i18next.t('offers-list-header-refcode')}</TableCell>
                {showEventTitle && <TableCell>{i18next.t('offers-list-header-title')}</TableCell>}
                <SortableBookingTableHeader
                  column="hotelName"
                  label={i18next.t('offers-list-header-hotel')}
                  currentSortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableBookingTableHeader
                  column="eventDate"
                  label={i18next.t('offers-list-header-from-to')}
                  currentSortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableBookingTableHeader
                  column="clientCompany"
                  label={i18next.t('offers-list-header-client')}
                  currentSortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableBookingTableHeader
                  column="status"
                  label={i18next.t('offers-list-header-status')}
                  currentSortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                {showDeleteReason && <TableCell>{i18next.t('offers-list-header-delete-reason')}</TableCell>}
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
          }
          headers={[
            i18next.t('offers-list-header-createdat'),
            i18next.t('offers-list-header-refcode'),
            i18next.t('offers-list-header-title'),
            i18next.t('offers-list-header-hotel'),
            i18next.t('offers-list-header-from-to'),
            i18next.t('offers-list-header-client'),
            i18next.t('offers-list-header-status'),
            i18next.t('offers-list-header-delete-reason'),
            '',
          ]}
          totalCount={data?.listWlOfferVersions?.length || 0}
          hidden={[false, false, !showEventTitle, false, false, false, false, !showDeleteReason, false]}
          rows={(offerVersionsData?.listWlOfferVersionsByIds || []).map((row, index) => [
            <FormatDateTime date={row.displayCreatedAt || row.createdAt} />,
            <span
              style={{ ...getBookingIdStyle(row.offer.source), cursor: 'pointer' }}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row);
                }
              }}
            >
              {row.offer.refCode}
            </span>,
            row.offer.source === EOfferSource.LISTER ? row.offer.bookingTitle || '' : '',
            <Link to={`/settings/hotels/${row.hotel.id}`} style={getHotelLinkStyle(row.offer.source)}>
              {row.hotel.name}
            </Link>,
            _formatDateRange(row),
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {row.offer.source !== EOfferSource.WIDGET && <CustomerTypeIndicator isPremium={row.offer.publicCompany?.isPremium} />}
              {row.client?.company}
            </div>,
            i18next.t(
              row.status === 'CR_WAITFORHOTELAPPROVAL' && row.offer.status === 'APPROVED'
                ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                : `enums-EOfferVersionStatus-${row.status}`,
            ),
            row.offer.deleteReason || '',
            _canArchive(row) && setShowDeleteDialog ? (
              <IconButton
                title={`${i18next.t('offers-list-delete')}`}
                onClick={e => {
                  e.stopPropagation();
                  setShowDeleteDialog(row);
                }}
              >
                <DeleteIcon />
              </IconButton>
            ) : (
              ''
            ),
            _showSendOfferReminderEmailClient(row) ? (
              <IconButton
                title={`${i18next.t('offer-reminder-email-send')}`}
                onClick={async e => {
                  e.stopPropagation();
                  try {
                    await sendOfferReminderEmailClient({
                      variables: {
                        offerVersionId: row.id,
                      },
                    });
                    dispatchMessage(dispatch, i18next.t('offer-reminder-email-sent'));
                  } catch (error: any) {
                    dispatchException(dispatch, error);
                  }
                }}
              >
                <SendIcon />
              </IconButton>
            ) : (
              ''
            ),
          ])}
          onRowClick={(index: number) => {
            if (!onRowClick) return;
            const row = (offerVersionsData?.listWlOfferVersionsByIds || [])[index];
            if (row) {
              onRowClick(row);
            }
          }}
        />
      )}
    </SimpleAccordion>
  );
};

/**
 * Main offers list page with pagination, search, and filtering.
 * Supports both WIDGET (SEM) and LISTER (EPILO) offer sources.
 * Shows combined view when combinedBookingView is enabled.
 */
export default function OffersList(props: { offerSource: EOfferSource }) {
  const { data: pdfImportFeature } = useQuery<{ pdfOfferImportEnabled: boolean }>(PDF_IMPORT_ENABLED_QUERY);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const filter = filterSelector();
  const user = userSelector()!;
  const bookingMode = bookingModeSelector();
  const combinedBookingView = combinedBookingViewSelector();
  const debugOffers = new URLSearchParams(window.location.search).get('debugOffers') === '1';

  const { data: semApprovalFlagData } = useQuery(OFFER_SEMINARGO_APPROVAL_ENABLED_QUERY, { fetchPolicy: 'network-only' });
  const offerSeminargoApprovalEnabled = semApprovalFlagData?.offerSeminargoApprovalEnabled ?? false;

  // Get current space based on filter (same logic as in main.tsx for booking mode selector)
  const getCurrentSpace = () => {
    if (filter?.spaceId) {
      return user.spaces.find(s => s.id === filter.spaceId);
    } else if (filter?.hotelId) {
      const hotel = user.hotels.find(h => h.id === filter.hotelId);
      return hotel?.space ? user.spaces.find(s => s.id === hotel.space.id) : null;
    }
    return null;
  };

  const currentSpace = getCurrentSpace();
  const hasEpilo = currentSpace?.hasEpilo || false;
  const hasSem = !!currentSpace?.activeSEMPlan;

  // Check if both booking modes are available (same logic as shouldShowBookingModeSelector)
  const hasBothModes = hasEpilo && hasSem;

  // Combined View Queries - fetch from the other source when combined view is active
  const shouldFetchCombined = combinedBookingView && hasBothModes;

  // Helper function to get link color based on source
  const getLinkColor = (source: EOfferSource) => {
    if (!combinedBookingView || !hasBothModes) return theme.palette.primary.main;
    return source === EOfferSource.LISTER ? '#AC2A6E' : '#266670';
  };

  const getHotelLinkStyle = (source: EOfferSource) => ({
    textDecoration: 'none',
    color: getLinkColor(source),
  });

  const getBookingIdStyle = (source: EOfferSource) => ({
    textDecoration: 'none',
    fontWeight: 'bold',
    color: getLinkColor(source),
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [refetchCounter, setRefetchCounter] = useState(0);

  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(prev => {
        if (prev !== value) {
          setRefetchCounter(c => c + 1);
          return value;
        }
        return prev;
      });
    }, 500),
    [],
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleteOfferVersionId, setDeleteOfferVersionId] = React.useState<number | null>(null);
  const [deleteReason, setDeleteReason] = React.useState('');

  const _handleShowDeleteDialog = (row: WLOfferVersionListShortFragment) => {
    setDeleteOfferVersionId(row.id);
    setDeleteReason('');
    setShowDeleteDialog(true);
  };

  const offerSourceFilter: EOfferSource | undefined = shouldFetchCombined ? undefined : props.offerSource;

  const queryOpenForInvoiceSemApprovalVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.INVOICE],
        versionStatus: [EOfferVersionStatus.IV_WAITFORSEMAPPROVAL],
        offerStatus: [EOfferStatus.INVOICE],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };
  const queryOpenForInvoiceSemApprovalSkip = !user.isSeminargo;

  const queryOfferSemApprovalVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: EOfferSource.LISTER,
        versionType: [EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.WAITFORSEMAPPROVAL],
        offerStatus: [EOfferStatus.PENDING],
        bookingStatus: [EHLBookingStatus.PENDING],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
    ],
  };
  const queryOfferSemApprovalSkip = !offerSeminargoApprovalEnabled || props.offerSource === EOfferSource.WIDGET;

  const queryOpenForInvoiceReviewVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.INVOICE],
        versionStatus: [EOfferVersionStatus.IV_WAITFORSEMAPPROVAL],
        offerStatus: [EOfferStatus.INVOICE],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };
  const queryOpenForInvoiceReviewSkip = user.isSeminargo;

  const queryOpenForEditingVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      //queryOpenToReserve
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.WAITFORHOTELRESERVATION],
        offerStatus: [EOfferStatus.CLIENTVERIFICATION, EOfferStatus.PENDING],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
      //queryopenToApprove
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.WAITFORHOTELAPPROVAL],
        offerStatus: [EOfferStatus.CLIENTVERIFICATION, EOfferStatus.PENDING],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
      //queryOpenToApproveCR
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.EDITING, EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL],
        offerStatus: [EOfferStatus.APPROVED, EOfferStatus.PENDING],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
      //queryOpenToEditCR
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.EDITING, EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.CR_EDITING],
        offerStatus: [EOfferStatus.APPROVED],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
      //queryOpenForInvoice
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.INVOICE],
        versionStatus: [EOfferVersionStatus.CIV_WAITFORHOTEL, EOfferVersionStatus.IV_WAITFORHOTEL],
        offerStatus: [EOfferStatus.INVOICE],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
      //queryOpenForCancellationConfirmation
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.RESERVATION_CONFIRMATION],
        versionStatus: [EOfferVersionStatus.CANCELLED_CLIENT_PENDING_HOTEL],
        offerStatus: [EOfferStatus.CLOSED],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED, EHLBookingStatus.CANCELLED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };

  const queryHotelEditingVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      //queryHotelEditing - SEM bookings where hotel is editing a reservation confirmation
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: EOfferSource.WIDGET,
        versionType: [EOfferVersionType.EDITING],
        versionStatus: [EOfferVersionStatus.APPROVED, EOfferVersionStatus.WAITFORHOTELAPPROVAL, EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL],
        offerStatus: [EOfferStatus.APPROVED, EOfferStatus.PENDING],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };

  const queryOpenWaitForClientVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.OFFER],
        versionStatus: [EOfferVersionStatus.WAITFORCLIENTAPPROVAL, EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL],
        offerStatus: [EOfferStatus.CLIENTVERIFICATION, EOfferStatus.PENDING, EOfferStatus.APPROVED],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
      // EDITING versions waiting for client approval (e.g., when hotel edits an offer)
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.EDITING],
        versionStatus: [EOfferVersionStatus.WAITFORCLIENTAPPROVAL, EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL],
        offerStatus: [EOfferStatus.PENDING, EOfferStatus.APPROVED],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };
  const queryApprovedVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.RESERVATION_CONFIRMATION],
        versionStatus: [EOfferVersionStatus.APPROVED],
        offerStatus: [EOfferStatus.APPROVED, EOfferStatus.ACTIVE],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
        excludeSemPastEventEnded: true,
      },
    ],
  };

  const queryArchivedVariables: ListWlOfferVersionsIdsQueryVariables = {
    filters: [
      //queryRejected
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.OFFER, EOfferVersionType.RESERVATION_CONFIRMATION, EOfferVersionType.DELETED, EOfferVersionType.EDITING],
        versionStatus: [EOfferVersionStatus.REJECTED_CLIENT, EOfferVersionStatus.REJECTED_HOTEL, EOfferVersionStatus.REJECTED_HOTEL_WITHDRAWN],
        offerStatus: [EOfferStatus.REJECTED],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED, EHLBookingStatus.DELETED, EHLBookingStatus.CANCELLED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
      //queryClosed
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionStatus: [
          EOfferVersionStatus.CLOSED,
          EOfferVersionStatus.IV_APPROVED,
          EOfferVersionStatus.IV_WAITFORCLIENTAPPROVAL,
          EOfferVersionStatus.APPROVED,
          EOfferVersionStatus.CANCELLED_CLIENT,
        ],
        offerStatus: [EOfferStatus.CLOSED, EOfferStatus.INVOICE],
        bookingStatus: [EHLBookingStatus.PENDING, EHLBookingStatus.APPROVED, EHLBookingStatus.DELETED, EHLBookingStatus.CANCELLED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
      //queryDeleted
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        versionType: [EOfferVersionType.OFFER, EOfferVersionType.EDITING, EOfferVersionType.RESERVATION_CONFIRMATION],
        versionStatus: [EOfferVersionStatus.DELETED],
        offerStatus: [EOfferStatus.DELETED],
        bookingStatus: [EHLBookingStatus.DELETED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
      //queryDeletedBookings
      {
        spaceId: (filter && filter.spaceId) || null,
        hotelId: (filter && filter.hotelId) || null,
        offerSource: offerSourceFilter,
        bookingStatus: [EHLBookingStatus.DELETED],
        searchQuery: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      },
    ],
  };

  const [deleteOfferVersion] = useMutation(OFFERVERSION_DELETE_MUTATION);

  const handleRowClick = (row: WLOfferVersionListShortFragment) => {
    // Check if the booking source matches the current mode
    const bookingSource = row.offer.source;
    if (bookingSource !== props.offerSource) {
      // Switch to the appropriate booking mode
      const targetMode = bookingSource === EOfferSource.WIDGET ? EBookingMode.SEM : EBookingMode.EPILO;
      dispatch(setBookingMode(targetMode));
    }

    if (
      row.status === EOfferVersionStatus.CIV_WAITFORHOTEL ||
      row.status === EOfferVersionStatus.IV_WAITFORHOTEL ||
      row.status === EOfferVersionStatus.IV_WAITFORSEMAPPROVAL ||
      row.status === EOfferVersionStatus.IV_WAITFORCLIENTAPPROVAL ||
      row.status === EOfferVersionStatus.IV_APPROVED
    ) {
      navigate(`/invoices/${row.offer.id}/${row.id}`);
    } else {
      navigate(`/offers/${row.offer.id}/${row.id}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>{i18next.t(`offers-list-page-title-${props.offerSource}`)}</title>
      </Helmet>
      {debugOffers && (
        <Alert
          severity="info"
          sx={{ mb: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', alignItems: 'flex-start' }}
          action={
            <Button
              size="small"
              onClick={async () => {
                const payload = {
                  url: window.location.pathname + window.location.search,
                  offerSource: props.offerSource,
                  bookingMode,
                  combinedBookingView,
                  shouldFetchCombined,
                  filter,
                  user: {
                    id: user?.id,
                    isRoot: user?.isRoot,
                    isSeminargo: user?.isSeminargo,
                    spaceId: user?.space?.id ?? null,
                  },
                };
                try {
                  await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
                  dispatchMessage(dispatch, String(i18next.t('offers-debug-copied')));
                } catch {
                  // ignore
                }
                Sentry.captureMessage('offers-debug', {
                  level: 'info',
                  contexts: {
                    offersDebug: payload as any,
                  },
                });
              }}
            >
              {i18next.t('offers-debug-copy-sentry')}
            </Button>
          }
        >
          {JSON.stringify(
            {
              url: window.location.pathname + window.location.search,
              offerSource: props.offerSource,
              combinedBookingView,
              shouldFetchCombined,
              filter,
              userId: user?.id,
            },
            null,
            2,
          )}
        </Alert>
      )}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            {props.offerSource === EOfferSource.LISTER && pdfImportFeature?.pdfOfferImportEnabled && (
              <Button variant="outlined" onClick={() => navigate('/offers/pdf-import')}>
                {i18next.t('pdf-import-button')}
              </Button>
            )}
            {props.offerSource === EOfferSource.WIDGET && (
              <Button
                variant="contained"
                onClick={async () => {
                  if (props.offerSource === EOfferSource.WIDGET) navigate('/offers/_create_widget');
                  if (props.offerSource === EOfferSource.LISTER) navigate('/offers/_create_lister');
                }}
              >
                <PlusIcon /> {i18next.t('offers-list-create-new')}
              </Button>
            )}
            {hasBothModes && (
              <FormControlLabel
                control={
                  <Checkbox checked={combinedBookingView} onChange={e => dispatch(setCombinedBookingView(e.target.checked))} color="primary" />
                }
                label={String(i18next.t('offers-list-combined-view'))}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <SearchIcon />
            <TextField
              label={i18next.t('search')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiInputBase-input': { py: 0.5 },
              }}
            />
          </Box>
        </Grid>
      </Grid>
      {user.isSeminargo && (props.offerSource === EOfferSource.LISTER || shouldFetchCombined) && (
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-seminargo-confirmation-${refetchCounter}`}
            title={i18next.t('offers-list-seminargo-confirmation')}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            queryVariables={queryOpenForInvoiceSemApprovalVariables}
            querySkip={queryOpenForInvoiceSemApprovalSkip}
            onRowClick={handleRowClick}
            setShowDeleteDialog={_handleShowDeleteDialog}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>
      )}
      {offerSeminargoApprovalEnabled && props.offerSource !== EOfferSource.WIDGET && (
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-offer-seminargo-confirmation-${refetchCounter}`}
            title={i18next.t(user.isSeminargo ? 'offers-list-offer-seminargo-confirmation' : 'offers-list-offer-seminargo-confirmation-hotel')}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            queryVariables={queryOfferSemApprovalVariables}
            querySkip={queryOfferSemApprovalSkip}
            onRowClick={handleRowClick}
            setShowDeleteDialog={_handleShowDeleteDialog}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {/* Hotel Editing Section */}
        {(props.offerSource === EOfferSource.WIDGET || shouldFetchCombined) && (
          <Grid item xs={12} sx={{ my: 2 }}>
            <OfferVersionListTable
              key={`offers-list-hotel-editing-${refetchCounter}`}
              title={i18next.t('offers-list-hotel-editing')}
              autoExpanded={true}
              showEventTitle={shouldFetchCombined}
              queryVariables={queryHotelEditingVariables}
              querySkip={false}
              onRowClick={handleRowClick}
              setShowDeleteDialog={_handleShowDeleteDialog}
              getHotelLinkStyle={getHotelLinkStyle}
              getBookingIdStyle={getBookingIdStyle}
            />
          </Grid>
        )}

        {/* Open/To Process Section */}
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-open-${refetchCounter}`}
            title={i18next.t('offers-list-open')}
            autoExpanded={true}
            defaultPageSize={20}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            queryVariables={queryOpenForEditingVariables}
            querySkip={false}
            onRowClick={handleRowClick}
            setShowDeleteDialog={_handleShowDeleteDialog}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>

        {/* Offers Section */}
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-offers-${refetchCounter}`}
            title={i18next.t('offers-list-offers')}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            queryVariables={queryOpenWaitForClientVariables}
            querySkip={false}
            onRowClick={handleRowClick}
            setShowDeleteDialog={_handleShowDeleteDialog}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>

        {/* Approved Section */}
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-reservations-${refetchCounter}`}
            title={i18next.t('offers-list-reservations')}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            queryVariables={queryApprovedVariables}
            querySkip={false}
            onRowClick={handleRowClick}
            setShowDeleteDialog={_handleShowDeleteDialog}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>

        {/* Invoice Review Section for non-seminargo users */}
        {!user.isSeminargo && (props.offerSource === EOfferSource.LISTER || shouldFetchCombined) && (
          <Grid item xs={12} sx={{ my: 2 }}>
            <OfferVersionListTable
              key={`offers-list-invoice-review-${refetchCounter}`}
              title={i18next.t('offers-list-invoice-review')}
              showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
              queryVariables={queryOpenForInvoiceReviewVariables}
              querySkip={queryOpenForInvoiceReviewSkip}
              onRowClick={handleRowClick}
              setShowDeleteDialog={_handleShowDeleteDialog}
              getHotelLinkStyle={getHotelLinkStyle}
              getBookingIdStyle={getBookingIdStyle}
            />
          </Grid>
        )}

        {/* Archive Section */}
        <Grid item xs={12} sx={{ my: 2 }}>
          <OfferVersionListTable
            key={`offers-list-archive-${refetchCounter}`}
            title={i18next.t('offers-list-archive')}
            showEventTitle={props.offerSource === EOfferSource.LISTER || shouldFetchCombined}
            showDeleteReason={true}
            queryVariables={queryArchivedVariables}
            querySkip={false}
            onRowClick={handleRowClick}
            getHotelLinkStyle={getHotelLinkStyle}
            getBookingIdStyle={getBookingIdStyle}
          />
        </Grid>
      </Grid>

      <ConfirmationDialog
        title={i18next.t('offerconfirm-delete-title')}
        open={showDeleteDialog}
        setOpen={setShowDeleteDialog}
        keepOpenOnConfirm={true}
        onConfirm={async () => {
          if (deleteReason.length === 0) {
            return;
          } else {
            try {
              await deleteOfferVersion({
                variables: {
                  offerVersionId: deleteOfferVersionId!,
                  deleteReason,
                },
                update: cache => EVICT_OFFER_QUERIES(cache),
                awaitRefetchQueries: true,
                refetchQueries: REFETCH_OFFER_QUERIES(),
              });
              setDeleteReason('');
              setShowDeleteDialog(false);
              dispatchMessage(dispatch, i18next.t('offerconfirm-delete-success'));
              setRefetchCounter(refetchCounter + 1);
            } catch (err) {
              dispatchException(dispatch, err);
            }
          }
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {i18next.t('offerconfirm-delete-text')}
          </Grid>
          <Grid item xs={12}>
            <TextField
              error={deleteReason.length === 0}
              fullWidth
              label={i18next.t('offerconfirm-delete-reason')}
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
            />
          </Grid>
        </Grid>
      </ConfirmationDialog>
    </>
  );
}
